import React from "react";
import { Card } from "@shared/ui/card";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { PageHeader } from "@shared/components/PageHeader";
import {
  Calendar,
  MapPin,
  Gauge,
  Info,
  Settings,
  ShieldCheck,
  CreditCard,
  Radio,
  Cpu,
  Wrench,
} from "lucide-react";
import { TechnicalSpecifications } from "./technicalSpecifications";
import { Link } from "react-router-dom";

interface OverviewTabProps {
  meterDetail: any;
  meterData: any;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  meterDetail,
  meterData,
}) => {
  console.log("meterData", meterData);
  // Calculate age based on installation date
  const calculateAge = (installDate: string) => {
    if (!installDate) return "NA";
    const install = new Date(installDate);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - install.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 30) return `${diffDays} days`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return `${diffMonths} months`;
    const diffYears = Math.floor(diffDays / 365);
    return `${diffYears} years`;
  };

  const installationFields = [
    { label: "Installation Date", value: meterDetail?.installedOn || "NA" },
    {
      label: "Age",
      value: meterDetail?.ageDisplay,
      tooltip:
        "Time elapsed since installation. Helps identify meters approaching end-of-life or requiring replacement.",
    },
    { label: "Premise", value: meterDetail?.premiseName || "NA" },
    {
      label: "Area / Subarea",
      value: meterDetail?.areaName,
      tooltip:
        "Geographic zone and subdivision for operational planning, route optimization, and regional reporting.",
    },
    { label: "Latitude", value: meterDetail?.lat || "NA" },
    { label: "Longitude", value: meterDetail?.long || "NA" },
    {
      label: "Linked Service Order",
      value: meterData?.serviceOrder ? (
        <Link
          to={`/service-orders/wx-master/details/${meterData?.serviceOrder?.id}`}
          className="text-blue-600 hover:underline font-medium"
        >
          {meterData?.serviceOrder?.name}
        </Link>
      ) : (
        "NA"
      ),
    },
  ];

  const smartMeterFields = [
    {
      label: "Communication Type",
      value:
        meterData?.additionalData?.meterTypeData?.communicationTypeDisplay ||
        "NA",
      icon: Cpu,
    },
    {
      label: "Sim/Module ID",
      value: meterData?.additionalData?.meterTypeData?.simModuleId || "NA",
      icon: Cpu,
    },
    {
      label: "HES",
      value: meterData?.additionalData?.meterTypeData?.hes || "NA",
      icon: Settings,
    },
  ];

  const amrFields = [
    {
      label: "Radio Number",
      value: meterData?.additionalData?.meterTypeData?.radioNumber || "NA",
      icon: Radio,
    },
    {
      label: "Read Source",
      value:
        meterData?.additionalData?.meterTypeData?.readSourceDisplay || "NA",
      icon: Settings,
    },
  ];

  const prepaidFields = [
    {
      label: "Current Balance",
      value: meterDetail?.current_balance || "NA",
      icon: CreditCard,
    },
    {
      label: "Low Balance Threshold",
      value: meterDetail?.low_balance_threshold || "NA",
      icon: ShieldCheck,
    },
    {
      label: "Disconnection Enabled",
      value: meterDetail?.disconnection_enabled || "NA",
      icon: Settings,
    },
    {
      label: "Disconnection Threshold",
      value: meterDetail?.disconnection_threshold || "NA",
      icon: ShieldCheck,
    },
    {
      label: "Emergency Credit Limit",
      value: meterDetail?.emergency_credit_limit || "NA",
      icon: CreditCard,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Installation and Premise Details */}
      <Card>
        <PageHeader
          title="Installation and Premise Details"
          subtitle="Location and deployment information"
          icon={MapPin}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-6">
          {installationFields.map((field) => (
            <ValueLabel
              key={field.label}
              label={field.label}
              value={field.value}
              tooltip={`View ${field.label}`}
            />
          ))}
        </div>
      </Card>

      {/* Smart Meter Config */}
      {meterDetail?.meterTypeDisplay === "Smart Meter" && (
        <Card>
          <PageHeader
            title="Smart Meter Configuration"
            subtitle="Connectivity and data transmission settings"
            icon={Cpu}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {smartMeterFields.map((field) => (
              <ValueLabel
                key={field.label}
                label={field.label}
                value={field.value}
              />
            ))}
          </div>
        </Card>
      )}

      {/* AMR / RF Details */}
      {meterDetail?.meterTypeDisplay === "AMR Meter" && (
        <Card>
          <PageHeader
            title="AMR / RF Details"
            subtitle="Advanced Meter Reading communication parameters"
            icon={Radio}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {amrFields.map((field) => (
              <ValueLabel
                key={field.label}
                label={field.label}
                value={field.value}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Prepaid Meter Config */}
      {meterDetail?.meterTypeDisplay === "Prepaid meter" && (
        <Card>
          <PageHeader
            title="Prepaid Meter Config"
            subtitle="Billing and credit management parameters"
            icon={CreditCard}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mt-6">
            {prepaidFields.map((field) => (
              <ValueLabel
                key={field.label}
                label={field.label}
                value={field.value}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Technical Specifications - Moved here from tab */}
      <div className="pt-4">
        <TechnicalSpecifications meterDetail={meterDetail} />
      </div>
    </div>
  );
};
